import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { marked } from "marked";
import { Twitter, Linkedin, Link2, Clock, Facebook, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArticleCard } from "@/components/public/ArticleCard";
import { NewsletterBlock } from "@/components/public/NewsletterBlock";
import { catLabel, fullDate, timeAgo } from "@/lib/format";
import { useSeo, organizationJsonLd } from "@/lib/seo";

marked.setOptions({ breaks: false, gfm: true });

export default function Article() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const canonical = a?.canonical_url || (typeof window !== "undefined" ? window.location.href : "");
  useSeo(a ? {
    title: a.seo_title || a.headline,
    description: a.seo_description || a.subtitle,
    image: a.og_image || a.featured_image,
    url: canonical,
    type: "article",
    jsonLd: [
      {
        "@type": "NewsArticle",
        headline: a.headline,
        description: a.subtitle,
        image: a.featured_image ? [a.featured_image] : undefined,
        datePublished: a.publication_date,
        dateModified: a.updated_date || a.publication_date,
        articleSection: catLabel(a.category),
        keywords: (a.keywords || a.tags || []).join(", "),
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        author: a.author ? { "@type": "Person", name: a.author.name, jobTitle: a.author.role } : organizationJsonLd(),
        publisher: {
          "@type": "Organization", name: "The Fin Desk",
          logo: { "@type": "ImageObject", url: "https://thefindesk.com/logo.png" },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
          { "@type": "ListItem", position: 2, name: catLabel(a.category), item: `${window.location.origin}/${a.category}` },
          { "@type": "ListItem", position: 3, name: a.headline },
        ],
      },
    ],
  } : null);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><h1 className="font-serif text-4xl">Story not found</h1><Link to="/" className="text-crimson mt-4 inline-block">Return home →</Link></div>;
  if (!a) return <div className="max-w-3xl mx-auto px-6 py-24 text-mutedink">Loading…</div>;

  const url = window.location.href;
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const nativeShare = () => {
    if (navigator.share) navigator.share({ title: a.headline, text: a.subtitle || a.headline, url }).catch(() => {});
    else copy();
  };
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(a.headline)}&url=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(a.headline + " " + url)}`,
  };

  return (
    <article className="pb-10" data-testid="article-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10">
        {/* Breadcrumb */}
        <div className="text-xs text-mutedink mb-4 font-medium">
          <Link to="/" className="hover:text-crimson">Home</Link> <span className="text-hair">/</span>{" "}
          <Link to={`/${a.category}`} className="hover:text-crimson">{catLabel(a.category)}</Link>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Link to={`/${a.category}`} className="text-xs font-bold uppercase tracking-[0.18em] text-crimson">{catLabel(a.category)}</Link>
          <span className="text-[10px] font-bold uppercase tracking-widest border border-hair text-ink2 px-1.5 py-0.5">{a.content_type}</span>
          {a.sponsored && <span className="text-[10px] font-bold uppercase tracking-widest bg-gold/10 text-gold px-1.5 py-0.5">Sponsored{a.sponsor ? ` · ${a.sponsor}` : ""}</span>}
        </div>

        <h1 className="font-serif font-bold tracking-tight leading-[1.1] text-3xl sm:text-4xl lg:text-5xl text-ink" data-testid="article-headline">{a.headline}</h1>
        {a.subtitle && <p className="mt-4 text-lg sm:text-xl text-ink2 leading-relaxed font-normal">{a.subtitle}</p>}

        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 py-4 border-y border-hair text-sm">
          {a.author && <Link to={`/authors/${a.author.slug}`} className="font-semibold text-ink hover:text-crimson">{a.author.name}</Link>}
          <span className="text-mutedink">{fullDate(a.publication_date)}</span>
          {a.updated_date && <span className="text-mutedink text-xs">Updated {timeAgo(a.updated_date)}</span>}
          <span className="text-mutedink flex items-center gap-1"><Clock size={13} />{a.reading_time} min read</span>
          <div className="flex items-center gap-1 ml-auto" data-testid="share-bar">
            <span className="text-[10px] font-bold uppercase tracking-widest text-mutedink mr-1 hidden sm:inline">Share</span>
            <a href={shareLinks.twitter} target="_blank" rel="noreferrer" aria-label="Share on X" title="Share on X" className="p-1.5 text-mutedink hover:text-crimson hover:bg-subtle rounded transition-colors" data-testid="share-twitter"><Twitter size={16} /></a>
            <a href={shareLinks.linkedin} target="_blank" rel="noreferrer" aria-label="Share on LinkedIn" title="Share on LinkedIn" className="p-1.5 text-mutedink hover:text-crimson hover:bg-subtle rounded transition-colors" data-testid="share-linkedin"><Linkedin size={16} /></a>
            <a href={shareLinks.facebook} target="_blank" rel="noreferrer" aria-label="Share on Facebook" title="Share on Facebook" className="p-1.5 text-mutedink hover:text-crimson hover:bg-subtle rounded transition-colors" data-testid="share-facebook"><Facebook size={16} /></a>
            <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer" aria-label="Share on WhatsApp" title="Share on WhatsApp" className="p-1.5 text-mutedink hover:text-crimson hover:bg-subtle rounded transition-colors" data-testid="share-whatsapp"><MessageCircle size={16} /></a>
            <button onClick={copy} aria-label="Copy link" title="Copy link" className="p-1.5 text-mutedink hover:text-crimson hover:bg-subtle rounded transition-colors" data-testid="share-copy"><Link2 size={16} /></button>
            <button onClick={nativeShare} aria-label="Share" title="More share options" className="p-1.5 text-mutedink hover:text-crimson hover:bg-subtle rounded transition-colors sm:hidden" data-testid="share-native"><Share2 size={16} /></button>
          </div>
        </div>
      </div>

      {/* Hero image */}
      {a.featured_image && (
        <figure className="max-w-4xl mx-auto px-4 sm:px-6 mt-8">
          <img src={a.featured_image} alt={a.headline} className="w-full h-auto object-cover" />
          {(a.image_caption || a.image_credit) && (
            <figcaption className="mt-2 text-xs text-mutedink flex justify-between">
              <span>{a.image_caption}</span>{a.image_credit && <span className="italic">{a.image_credit}</span>}
            </figcaption>
          )}
        </figure>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-10">
        {a.why_this_matters && (
          <div className="bg-subtle border-l-2 border-crimson p-5 mb-8">
            <div className="text-[11px] font-bold uppercase tracking-widest text-crimson mb-1">Why this matters</div>
            <p className="text-ink2 text-sm leading-relaxed">{a.why_this_matters}</p>
          </div>
        )}

        <div className="prose-editorial" dangerouslySetInnerHTML={{ __html: marked.parse(a.body || "") }} />

        {/* Tags */}
        {a.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {a.tags.map((t) => <span key={t} className="text-xs font-medium text-ink2 bg-subtle px-2.5 py-1">{t}</span>)}
          </div>
        )}

        {/* Sources */}
        {(a.sources?.length > 0 || a.source_urls?.length > 0) && (
          <div className="mt-10 border-t border-hair pt-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-crimson mb-3">Sources & References</div>
            <ul className="space-y-1.5 text-sm">
              {(a.sources?.length ? a.sources.map(s => s.url) : a.source_urls).map((u, i) => (
                <li key={i}><a href={u} target="_blank" rel="noreferrer" className="text-crimson break-all hover:underline">{u}</a></li>
              ))}
            </ul>
          </div>
        )}

        {/* Company references */}
        {a.companies?.length > 0 && (
          <div className="mt-8 border-t border-hair pt-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-crimson mb-3">Companies in this story</div>
            <div className="flex flex-wrap gap-3">
              {a.companies.map((c) => (
                <Link key={c.id} to={`/companies/${c.slug}`} className="border border-hair px-3 py-2 hover:border-crimson transition-colors">
                  <div className="font-semibold text-ink text-sm">{c.name}</div>
                  <div className="text-xs text-mutedink">{c.company_type} · {c.country}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Author bio */}
        {a.author && (
          <div className="mt-12 bg-subtle p-6 border-l-2 border-ink">
            <div className="text-[11px] font-bold uppercase tracking-widest text-crimson mb-1">About the Author</div>
            <Link to={`/authors/${a.author.slug}`} className="font-serif text-2xl text-ink hover:text-crimson">{a.author.name}</Link>
            <div className="text-xs text-mutedink mb-2">{a.author.role}</div>
            <p className="text-sm text-ink2 leading-relaxed">{a.author.bio}</p>
          </div>
        )}
      </div>

      {/* Related */}
      {a.related?.length > 0 && (
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 mt-16">
          <div className="border-b-2 border-ink pb-2 mb-6"><h2 className="font-serif font-bold text-2xl">Related Stories</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {a.related.map((r) => <ArticleCard key={r.id} a={r} variant="standard" />)}
          </div>
        </div>
      )}

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 mt-16"><NewsletterBlock /></div>
    </article>
  );
}
